import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import toast from 'react-hot-toast';

const Waitlist = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    role: '',
    interests: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const interests = [
    'Deal Sourcing',
    'Due Diligence',
    'Portfolio Management',
    'Market Analytics',
    'Risk Assessment',
    'Performance Tracking'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleInterestToggle = (interest) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Prepare email template parameters
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        company: formData.company || 'Not provided',
        role: formData.role || 'Not provided',
        interests: formData.interests.length > 0 
          ? formData.interests.join(', ') 
          : 'None selected',
        timestamp: new Date().toLocaleString('en-US', {
          dateStyle: 'medium',
          timeStyle: 'short'
        }),
      };

      // Send notification to admin
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_ADMIN_TEMPLATE_ID,
        templateParams,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      // Send confirmation to user
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_USER_TEMPLATE_ID,
        templateParams,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      toast.success('Successfully joined! Check your email for confirmation.', {
        duration: 5000,
        position: 'top-center',
        icon: '🎉',
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        company: '',
        role: '',
        interests: []
      });
      
      onClose();
    } catch (error) {
      console.error('EmailJS Error:', error);
      toast.error('Failed to join waitlist. Please try again or contact us directly.', {
        duration: 5000,
        position: 'top-center',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        {/* Header */}
        <div className="modal-header">
          <div>
            <h2 className="modal-title">Join VentureOS Waitlist</h2>
            <p className="modal-subtitle">Be among the first to access the future of venture capital</p>
          </div>
          <button onClick={onClose} className="close-button">
            <svg style={{width: '24px', height: '24px'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="form-container">
          {/* Personal Info */}
          <div className="form-group">
            <div className="form-row">
              <div>
                <label className="form-label">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="form-label">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                  placeholder="john@example.com"
                />
              </div>
            </div>
          </div>

          {/* Professional Info */}
          <div className="form-group">
            <div className="form-row">
              <div>
                <label className="form-label">
                  Company/Fund
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Acme Ventures"
                />
              </div>
              <div>
                <label className="form-label">
                  Role
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  <option value="">Select your role</option>
                  <option value="General Partner">General Partner</option>
                  <option value="Managing Partner">Managing Partner</option>
                  <option value="Principal">Principal</option>
                  <option value="Associate">Associate</option>
                  <option value="Analyst">Analyst</option>
                  <option value="Investment Manager">Investment Manager</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
          </div>

          {/* Interests */}
          <div className="form-group">
            <label className="form-label">
              What interests you most about VentureOS? (Select all that apply)
            </label>
            <div className="interests-grid">
              {interests.map((interest) => (
                <button
                  key={interest}
                  type="button"
                  onClick={() => handleInterestToggle(interest)}
                  className={`interest-button ${
                    formData.interests.includes(interest) ? 'selected' : ''
                  }`}
                >
                  {interest}
                </button>
              ))}
            </div>
          </div>

          {/* Benefits */}
          <div className="benefits-section">
            <h3 className="benefits-title">Early Access Benefits:</h3>
            <ul className="benefits-list">
              <li>
                <svg className="check-icon" fill="none" stroke="#10b981" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Priority access to VentureOS platform
              </li>
              <li>
                <svg className="check-icon" fill="none" stroke="#10b981" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Exclusive beta features and early feedback opportunities
              </li>
              <li>
                <svg className="check-icon" fill="none" stroke="#10b981" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Special launch pricing and extended trial period
              </li>
              <li>
                <svg className="check-icon" fill="none" stroke="#10b981" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Direct line to our product team for feature requests
              </li>
            </ul>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting || !formData.name || !formData.email}
            className="submit-button"
          >
            {isSubmitting ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
                Sending Email...
              </div>
            ) : (
              'Join the Waitlist'
            )}
          </button>

          <p className="form-footer">
            By joining, you agree to receive updates about VentureOS. We respect your privacy and won't spam you.
          </p>
        </form>
      </div>
    </div>
  );
};

export default Waitlist;
